import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    AutoFillSetupStep,
    DEFAULT_SUPPLIERS,
    SupplierPreferenceEntry,
    SupplierPreferenceItem
} from '../../models/purchase-manager-config.model';
import { PriceDiscoveryService } from '../../services/price-discovery.service';

/**
 * Auto-Fill Smart Cart Component
 * Displays the auto-fill smart cart banner and handles supplier preference configuration.
 *
 * DB structure (purchasemanagerConfig.supplierPreferrence):
 *   Array<{ id: string; autoFill: boolean }>
 *   - Array ORDER  = drag-sort order (used by price discovery for column ordering)
 *   - autoFill     = checkbox state (true = participates in auto-fill cart)
 */
@Component({
    selector: 'app-auto-fill-smart-cart',
    templateUrl: './auto-fill-smart-cart.component.html',
    styleUrls: ['./auto-fill-smart-cart.component.scss']
})
export class AutoFillSmartCartComponent implements OnInit, OnChanges, OnDestroy {

    @Input() entityId: string = '';
    @Output() configChanged = new EventEmitter<SupplierPreferenceEntry[]>();

    // Modal state
    isModalOpen = false;
    currentStep: AutoFillSetupStep = AutoFillSetupStep.INTRO;

    // Configuration state
    isConfigured = false;
    supplierPreferences: SupplierPreferenceItem[] = [];
    tempSupplierPreferences: SupplierPreferenceItem[] = [];

    // Loading state
    isLoading = false;
    isSaving = false;

    // Enum reference for template
    SetupStep = AutoFillSetupStep;

    private destroy$ = new Subject<void>();

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnInit(): void {
        this.loadConfiguration();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['entityId'] && !changes['entityId'].firstChange && changes['entityId'].currentValue) {
            // Entity changed — reset state and reload config for the new entity
            this.isConfigured = false;
            this.supplierPreferences = DEFAULT_SUPPLIERS.map(s => ({ ...s }));
            this.tempSupplierPreferences = [];
            this.loadConfiguration();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Load existing configuration from API and reconstruct full UI state.
     * The saved array always contains all 4 suppliers in drag-sort order with autoFill flags.
     */
    loadConfiguration(): void {
        if (!this.entityId) return;

        this.isLoading = true;
        this.priceDiscoveryService.getPurchaseManagerConfig(this.entityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (config: any) => {
                    const activeIds = ['local'];
                    if (config?.isHyperpureEnabled) activeIds.push('hyperpure');
                    if (config?.isUdaanEnabled) activeIds.push('udaan');
                    if (config?.isDmartEnabled) activeIds.push('dmart');

                    // DB key: purchasemanagerConfig.supplierPreferrence (array of { id, autoFill })
                    const saved: SupplierPreferenceEntry[] =
                        config?.purchasemanagerConfig?.supplierPreferrence || [];

                    if (saved.length > 0) {
                        this.isConfigured = true;
                        this.supplierPreferences = this.mapSavedToItems(saved, activeIds);
                    } else {
                        this.isConfigured = false;
                        this.supplierPreferences = DEFAULT_SUPPLIERS
                            .filter(s => activeIds.includes(s.id))
                            .map((s, index) => ({ ...s, position: index }));
                    }
                    this.isLoading = false;
                },
                (error: any) => {
                    console.error('Failed to load purchase manager config', error);
                    this.supplierPreferences = DEFAULT_SUPPLIERS.map(s => ({ ...s }));
                    this.isLoading = false;
                }
            );
    }

    /**
     * Map saved DB entries back to UI items.
     * Saved entries already include all suppliers in drag-sort order with autoFill flags.
     * Any supplier missing from saved (e.g. newly added) is appended at the end with autoFill=true.
     */
    private mapSavedToItems(saved: SupplierPreferenceEntry[], activeIds: string[]): SupplierPreferenceItem[] {
        const result: SupplierPreferenceItem[] = [];

        // Restore saved suppliers in saved order with their autoFill state
        saved.forEach((entry) => {
            if (activeIds.includes(entry.name)) {
                const meta = DEFAULT_SUPPLIERS.find(s => s.id === entry.name);
                if (meta) {
                    result.push({ ...meta, autoFill: entry.autoFill, position: result.length });
                }
            }
        });

        // Append any new suppliers not yet in DB (default autoFill=true)
        DEFAULT_SUPPLIERS.forEach(supplier => {
            if (activeIds.includes(supplier.id) && !saved.find(e => e.name === supplier.id)) {
                result.push({ ...supplier, autoFill: true, position: result.length });
            }
        });

        return result;
    }

    /**
     * Convert UI items to the DB payload format.
     * Always saves ALL suppliers to preserve their order and autoFill state.
     */
    private toDbEntries(items: SupplierPreferenceItem[]): SupplierPreferenceEntry[] {
        return items.map(item => ({ name: item.id, autoFill: item.autoFill }));
    }

    /**
     * Display text for the status bar — lists only autoFill-enabled suppliers.
     */
    get configDisplayText(): string {
        const autoFillSuppliers = this.supplierPreferences.filter(s => s.autoFill);
        if (autoFillSuppliers.length === this.supplierPreferences.length && this.supplierPreferences.length > 0) {
            return 'All Suppliers';
        }
        if (autoFillSuppliers.length === 0) {
            return 'No suppliers selected';
        }
        return autoFillSuppliers.map(s => s.name).join(', ');
    }

    /**
     * Open configuration modal — Step 1 for first-time, Step 2 for modify.
     */
    openConfigureModal(): void {
        this.tempSupplierPreferences = this.supplierPreferences.map(s => ({ ...s }));
        this.currentStep = this.isConfigured ? AutoFillSetupStep.CONFIGURE : AutoFillSetupStep.INTRO;
        this.isModalOpen = true;
    }

    /**
     * Open modify setting — goes directly to Step 2 with current saved state.
     */
    openModifySettings(): void {
        this.tempSupplierPreferences = this.supplierPreferences.map(s => ({ ...s }));
        this.currentStep = AutoFillSetupStep.CONFIGURE;
        this.isModalOpen = true;
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.currentStep = AutoFillSetupStep.INTRO;
    }

    continueToSetup(): void {
        this.currentStep = AutoFillSetupStep.CONFIGURE;
    }

    onSupplierDrop(event: CdkDragDrop<SupplierPreferenceItem[]>): void {
        moveItemInArray(this.tempSupplierPreferences, event.previousIndex, event.currentIndex);
        this.tempSupplierPreferences.forEach((item, index) => { item.position = index; });
    }

    toggleSupplier(supplier: SupplierPreferenceItem): void {
        supplier.autoFill = !supplier.autoFill;
    }

    /**
     * Save configuration.
     * Sends all 4 suppliers in current drag-sort order with their autoFill flags.
     * At least one must be autoFill=true.
     */
    finishSetup(): void {
        const hasAny = this.tempSupplierPreferences.some(s => s.autoFill);
        if (!hasAny) {
            this.sharedService.warningAlert('Please select at least one supplier');
            return;
        }

        const payload = this.toDbEntries(this.tempSupplierPreferences);

        this.isSaving = true;
        this.priceDiscoveryService.updatePurchaseManagerConfig(this.entityId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                () => {
                    this.supplierPreferences = this.tempSupplierPreferences.map(s => ({ ...s }));
                    this.isConfigured = true;
                    this.isSaving = false;
                    this.closeModal();
                    this.sharedService.successAlert('Supplier preferences saved successfully!');
                    this.configChanged.emit(payload);
                },
                (error: any) => {
                    console.error('Failed to save configuration', error);
                    this.isSaving = false;
                    this.sharedService.errorAlert('Failed to save preferences. Please try again.');
                }
            );
    }

    get hasEnabledSuppliers(): boolean {
        return this.tempSupplierPreferences.some(s => s.autoFill);
    }
}
